import { useState, useEffect } from 'react';

import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch a single variation (CollectionItem) for a school directly from the
 * dedicated endpoint. Use this for PDP/deep-link loads to avoid pulling the
 * full school variation list.
 *
 * Backed by GET /api/schools/{brandSlug}/{schoolSlug}/variations/{variationId}.
 */
export default function useSchoolVariation(brandSlug, schoolSlug, variationId, className = null, shopSlug = null) {
  const [data, setData]       = useState({ school: null, variation: null });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!brandSlug || !schoolSlug || variationId == null) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (className) params.set('class', className);
        if (shopSlug) params.set('shop', shopSlug);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const url = `${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}`
          + `/${encodeURIComponent(schoolSlug)}/variations/${encodeURIComponent(variationId)}${qs}`;
        const res = await fetch(url, { signal: controller.signal });

        if (!res.ok) throw new Error(`School variation API returned ${res.status}`);

        const json = await res.json();
        if (!cancelled) {
          setData({
            school: json.school ?? null,
            variation: json.variation ?? null,
          });
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          console.error(`[useSchoolVariation] Failed for "${brandSlug}/${schoolSlug}/${variationId}":`, err);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, schoolSlug, variationId, className, shopSlug]);

  return { ...data, loading, error };
}
